/** Canonical section order for crash forms (nav tree, form list, payload). */
export const CRASH_FORM_SECTION_KEYS = ['crash', 'road', 'units', 'people'];

/** Police org id — merged crashes use Police as merge master configuration. */
export const MERGED_CRASH_INIT_ORGANIZATION_ID = 1;

/** Basic org ids whose form fields are unioned when viewing a validated merged crash. */
export const MERGED_CRASH_CONFIGURATION_ORG_IDS = [1, 2, 3];

/**
 * Merges field elements from multiple section configs, deduplicating by `label`.
 * Elements without a label (e.g. dividers) are kept from the first config only.
 *
 * @param {Array<Array<{ label?: string }> | undefined>} elementLists
 * @returns {Array<{ label?: string }>}
 */
export function mergeConfigurationElements(elementLists) {
  const seenLabels = new Set();
  const merged = [];

  for (const elements of elementLists) {
    if (!Array.isArray(elements)) continue;
    for (const element of elements) {
      const label = element?.label;
      if (label) {
        if (seenLabels.has(label)) continue;
        seenLabels.add(label);
      } else if (merged.length > 0) {
        continue;
      }
      merged.push(element);
    }
  }

  return merged;
}

/**
 * Unions form `configuration` objects from Police, Hospital, and Insurance inits.
 * Section metadata comes from the first config; `elements` are merged by field label.
 *
 * @param {Array<Record<string, { elements?: unknown[], position?: number, hierarchy?: number }>>} configurations
 * @returns {Record<string, unknown>}
 */
export function mergeFormConfigurations(configurations) {
  const valid = (configurations ?? []).filter((c) => c && typeof c === 'object');
  if (valid.length === 0) return {};
  if (valid.length === 1) return { ...valid[0] };

  const sectionKeys = new Set();
  valid.forEach((config) => {
    Object.keys(config).forEach((key) => sectionKeys.add(key));
  });

  const merged = {};
  sectionKeys.forEach((sectionKey) => {
    const sections = valid.
    map((config) => config[sectionKey]).
    filter((section) => section && typeof section === 'object');
    if (sections.length === 0) return;

    merged[sectionKey] = {
      ...sections[0],
      elements: mergeConfigurationElements(sections.map((section) => section.elements)),
    };
  });

  return merged;
}

/**
 * Shallow-merges `defaults` from multiple inits so merged crashes include all field keys.
 *
 * @param {Array<Record<string, unknown>>} defaultsList
 * @returns {Record<string, unknown>}
 */
export function mergeFormDefaults(defaultsList) {
  const valid = (defaultsList ?? []).filter((d) => d && typeof d === 'object');
  if (valid.length === 0) return {};
  if (valid.length === 1) return { ...valid[0] };

  const merged = { ...valid[0] };
  for (let i = 1; i < valid.length; i += 1) {
    const next = valid[i];
    Object.keys(next).forEach((key) => {
      const existing = merged[key];
      const incoming = next[key];
      if (existing == null) {
        merged[key] = incoming;
        return;
      }
      if (
      incoming != null &&
      typeof existing === 'object' &&
      typeof incoming === 'object' &&
      !Array.isArray(existing) &&
      !Array.isArray(incoming))
      {
        merged[key] = { ...existing, ...incoming };
      }
    });
  }

  return merged;
}

const CRASH_PAYLOAD_META_KEYS = new Set(['is_closed', 'done', 'crash_id']);

/**
 * Sorts section keys in canonical form order; unknown keys are appended at the end.
 *
 * @param {string[]} keys
 * @param {string[]} [priorityKeys]
 * @returns {string[]}
 */
export function orderCrashFormSectionKeys(
  keys,
  priorityKeys = CRASH_FORM_SECTION_KEYS,
) {
  const keySet = new Set(keys);
  const ordered = priorityKeys.filter((key) => keySet.has(key));
  keys.forEach((key) => {
    if (!ordered.includes(key)) ordered.push(key);
  });
  return ordered;
}

/**
 * Builds a crash payload object with sections in canonical order (not alphabetical).
 *
 * @param {Record<string, unknown>} crash
 * @param {string[]} [sectionKeys]
 * @returns {Record<string, unknown>}
 */
export function buildOrderedCrashPayload(
  crash,
  sectionKeys = CRASH_FORM_SECTION_KEYS,
) {
  if (!crash || typeof crash !== 'object') return {};

  const ordered = {};
  orderCrashFormSectionKeys(Object.keys(crash), sectionKeys).forEach((key) => {
    if (CRASH_PAYLOAD_META_KEYS.has(key)) return;
    ordered[key] = crash[key];
  });

  CRASH_PAYLOAD_META_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(crash, key)) {
      ordered[key] = crash[key];
    }
  });

  return ordered;
}

/**
 * Resolves form section type from a react-hook-form path (e.g. `units.0` → `units`).
 *
 * @param {string | undefined} formPath
 * @returns {string}
 */
export function resolveFormTypeFromPath(formPath) {
  if (!formPath) return 'crash';
  const segments = String(formPath).split('.');
  const nested = segments.slice(-2, -1)[0];
  return nested ?? segments[0];
}
